import {
  FC,
  MouseEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import clsx from "clsx";
import { Typography, useTheme } from "@mui/material";

import { useClickOutside } from "hooks/click-outside";

import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import ChevronDown from "icons/ChevronDown";
import Search from "icons/Search";

import { Input } from "../Input";
import { Button } from "components/common/Button";

import {
  StyledSelectContainer,
  StyledSelectLabel,
  StyledSelectTitle,
  StyledSelectError,
  StyledSelectAddNewOptionBtnField,
  StyledSelectAddNewOptionField,
  StyledSelectExtraField,
  StyledSelectEmptyData,
  StyledSelect,
  StyledSelectArrow,
  StyledSelectContent,
  StyledSelectList,
  StyledSelectItem,
  StyledSelectField,
} from "styles/components/inputs/Select";

type SelectOption = {
  value: string;
  label: string;
};

interface SelectProps {
  id?: string;
  options?: SelectOption[];
  value?: string;
  defaultValue?: string;
  required?: boolean;
  label?: string;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  error?: boolean;
  errorText?: string;
  dataTestId?: string;
  color?: "white" | "grey";
  withSearch?: boolean;
  withAddNewOption?: boolean;
  searchPlaceholder?: string;
  addNewOptionPlaceholder?: string;
  addNewOptionBtnText?: string;

  onChange?: (value: string) => void;
}

const initialSelectOptions: SelectOption[] = [
  { value: "first", label: "First item" },
  { value: "second", label: "Second item" },
];

const Select: FC<SelectProps> = ({
  id,
  options = initialSelectOptions,
  label = "",
  required,
  value,
  dataTestId,
  className,
  placeholder = "Placeholder",
  color,
  error = false,
  errorText = "Error text",
  searchPlaceholder = "Search...",
  addNewOptionBtnText = "Add a new option",
  addNewOptionPlaceholder = "Add a new option",
  withSearch = false,
  withAddNewOption = false,
  disabled = false,
  onChange,
}) => {
  const theme = useTheme();

  const [actualOptions, setActualOptions] = useState<SelectOption[]>(
    options ?? []
  );
  const [selectedOption, setSelectedOption] = useState<string>(value ?? "");
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [searchedValue, setSearchedValue] = useState<string>("");
  const [newOptionName, setNewOptionName] = useState<string>("");
  const [newOptionFieldIsVisible, setNewOptionFieldIsVisible] =
    useState<boolean>(false);
  const [newOptionNameError, setNewOptionNameError] = useState<boolean>(false);

  useEffect(() => {
    if (options && actualOptions.length === 0) {
      setActualOptions(options);
    }
  }, [actualOptions.length, options]);

  useEffect(() => {
    if (value) {
      setSelectedOption(value);
    }
  }, [value]);

  const selectRef = useRef<HTMLDivElement>(null);

  const wrapClassName = clsx(["select-container", className]);

  const selectClassName = clsx([
    "select",
    {
      select_open: isOpen && !disabled,
      select_grey: color === "grey",
      select_white: color === "white",
      select_error: error,
      select_disabled: disabled,
    },
  ]);

  const errorClassName = clsx("select-error", {
    "select-error_visible": error,
  });

  const withExtraField = useMemo(
    () => !!withSearch || !!withAddNewOption,
    [withAddNewOption, withSearch]
  );

  const renderedLabel = useMemo(
    () =>
      label ? (
        <StyledSelectLabel
          htmlFor={id}
          data-testid={dataTestId && `${dataTestId}-label`}
        >
          {label}
          {required ? <span className="select__marker">*</span> : null}
        </StyledSelectLabel>
      ) : null,
    [dataTestId, id, label, required]
  );

  const chosenTitle = useMemo(
    () => actualOptions.find(({ value }) => value === selectedOption)?.label,
    [actualOptions, selectedOption]
  );

  const handleToggleSelect = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation();

      setIsOpen((prev) => !prev);
    },
    []
  );

  const handleSelectClose = useCallback(() => {
    setIsOpen(false);

    setSearchedValue("");

    setNewOptionFieldIsVisible(false);

    setNewOptionName("");

    setNewOptionNameError(false);
  }, []);

  useClickOutside({
    ref: selectRef,
    onClickOutside: handleSelectClose,
    closeOnEsc: isOpen,
    closeOnOutside: true,
    isOpen: isOpen,
  });

  const handleSelectChange = useCallback(
    (value: string) => () => {
      setSelectedOption(value);
      handleSelectClose();

      onChange?.(value);
    },
    [handleSelectClose, onChange]
  );

  const showNewOptionField = useCallback(
    () => setNewOptionFieldIsVisible(true),
    []
  );

  const handleSearch = useCallback(
    (value: string) => setSearchedValue(value),
    []
  );

  const handleNewOptionNameChange = useCallback((value: string) => {
    setNewOptionNameError(false);
    setNewOptionName(value);
  }, []);

  const handleNewOptionAdd = useCallback(() => {
    if (newOptionName) {
      const newOption = {
        label: newOptionName,
        value: newOptionName,
      };

      const duplicate = actualOptions.find(
        ({ value }) =>
          value.toLocaleLowerCase() === newOptionName.toLocaleLowerCase()
      );

      if (duplicate) {
        setNewOptionNameError(true);
      } else {
        setActualOptions((prev) => [...prev, newOption]);

        setSelectedOption(newOptionName.toLocaleLowerCase());

        handleSelectClose();

        onChange?.(newOptionName.toLocaleLowerCase());
      }

      setNewOptionName("");
    }
  }, [actualOptions, handleSelectClose, newOptionName, onChange]);

  const renderedSearchField = useMemo(
    () =>
      withSearch ? (
        <Input
          prefix={<Search />}
          label=""
          withErrorField={false}
          placeholder={searchPlaceholder}
          inputClassName="select-search"
          onChange={handleSearch}
          value={searchedValue}
        />
      ) : null,
    [handleSearch, searchPlaceholder, searchedValue, withSearch]
  );

  const filteredOptions = useMemo(() => {
    return searchedValue && searchedValue.length > 0
      ? actualOptions.filter(({ label }) =>
          label.toLocaleLowerCase().includes(searchedValue.toLocaleLowerCase())
        )
      : actualOptions;
  }, [actualOptions, searchedValue]);

  const renderedAddNewOptionBtnField = useMemo(
    () =>
      withAddNewOption ? (
        <StyledSelectAddNewOptionBtnField>
          <Button
            variant="text"
            startIcon={<AddOutlinedIcon />}
            onClick={showNewOptionField}
          >
            {addNewOptionBtnText}
          </Button>
        </StyledSelectAddNewOptionBtnField>
      ) : null,
    [addNewOptionBtnText, showNewOptionField, withAddNewOption]
  );

  const renderedAddNewOptionField = useMemo(
    () =>
      newOptionFieldIsVisible ? (
        <StyledSelectAddNewOptionField>
          <Input
            label=""
            withErrorField={false}
            placeholder={addNewOptionPlaceholder}
            inputClassName="select-new-option"
            onChange={handleNewOptionNameChange}
            value={newOptionName}
            autoFocus
            error={newOptionNameError}
          />
          <Button
            variant="text"
            onClick={handleNewOptionAdd}
            underlined
            disabled={!newOptionName}
          >
            Save
          </Button>
        </StyledSelectAddNewOptionField>
      ) : null,
    [
      addNewOptionPlaceholder,
      handleNewOptionAdd,
      handleNewOptionNameChange,
      newOptionFieldIsVisible,
      newOptionName,
      newOptionNameError,
    ]
  );

  const renderedExtraField = useMemo(
    () =>
      withExtraField ? (
        <StyledSelectExtraField grey={color === "grey" ? 1 : 0}>
          {renderedAddNewOptionBtnField}
          {renderedAddNewOptionField}
          {renderedSearchField}
        </StyledSelectExtraField>
      ) : null,
    [
      color,
      renderedAddNewOptionBtnField,
      renderedAddNewOptionField,
      renderedSearchField,
      withExtraField,
    ]
  );

  return (
    <StyledSelectContainer className={wrapClassName} data-testid={dataTestId}>
      {renderedLabel}
      <StyledSelectField ref={selectRef}>
        <StyledSelect
          className={selectClassName}
          onClick={handleToggleSelect}
          id={id}
          data-testid={dataTestId && `${dataTestId}-select`}
        >
          <StyledSelectTitle
            variant="bodyL"
            color={
              disabled || !chosenTitle
                ? theme.palette.customGrey.main
                : theme.palette.common.black
            }
          >
            {chosenTitle || placeholder}
          </StyledSelectTitle>
          <StyledSelectArrow className="select__arrow">
            <ChevronDown />
          </StyledSelectArrow>
        </StyledSelect>
        <StyledSelectContent className="select__content">
          {renderedExtraField}
          <StyledSelectList className="select__list">
            {filteredOptions.length > 0 ? (
              filteredOptions?.map(({ value, label }) => (
                <StyledSelectItem
                  onClick={handleSelectChange(value)}
                  key={value}
                  grey={color === "grey" ? 1 : 0}
                  className={clsx("select__option", {
                    select__option_active: value === selectedOption,
                  })}
                >
                  {label}
                </StyledSelectItem>
              ))
            ) : (
              <StyledSelectEmptyData>
                <Typography
                  variant="bodyM"
                  color={theme.palette.customGrey.main}
                >
                  No data
                </Typography>
              </StyledSelectEmptyData>
            )}
          </StyledSelectList>
        </StyledSelectContent>
      </StyledSelectField>
      <StyledSelectError
        variant="bodyM"
        fontWeight={300}
        className={errorClassName}
        data-testid={dataTestId && `${dataTestId}-error`}
      >
        {errorText}
      </StyledSelectError>
    </StyledSelectContainer>
  );
};

export default Select;
